import appSettings from '@/app-settings.json';
import { API_SENDER_MODE_OPTIONS } from '@/constants';
import Auth from '@aws-amplify/auth';
import { Map } from 'immutable';
import { BaseSender } from './base-sender';

/**
 * @override getTokens() parent method to invert the order for the tokens.
 * @override getDataDomain() to set the correct domain for the server.
 */
export class StrOracleApiSender extends BaseSender {
  async getTokens() {
    // eslint-disable-line class-methods-use-this
    // getting the current session will refresh the token
    const cognitoUserSession = await Auth.currentSession();
    const accessToken =
      cognitoUserSession[BaseSender.tokenTypes.ACCESS_TOKEN].jwtToken;
    const idToken = cognitoUserSession[BaseSender.tokenTypes.ID_TOKEN].jwtToken;
    return {
      authorization: `Bearer ${idToken}`,
      xAmzSecurityToken: accessToken,
    };
  }

  mode = Map();

  setIncludeCities(includeCities) {
    const { SHOW_ALL } = API_SENDER_MODE_OPTIONS;
    this.mode = includeCities
      ? this.mode.set(SHOW_ALL, true)
      : this.mode.delete(SHOW_ALL);
  }

  getIncludeCities() {
    return Boolean(this.mode.get(API_SENDER_MODE_OPTIONS.SHOW_ALL));
  }

  getModeKeyValues() {
    return this.mode.toJSON();
  }

  // eslint-disable-next-line class-methods-use-this
  getDataDomain() {
    return `${appSettings.apiDomain}/dashboard/v1/${BaseSender.currentCity}`;
  }

  // eslint-disable-next-line class-methods-use-this
  getDataDomainNoCity() {
    return `${appSettings.apiDomain}/dashboard/v1/`;
  }

  /**
   * Performs a GET on an endpoint of STR Oracle API.
   *
   * @param {String} path The API route. ("/property", "/property/", "property", "/property/tot", etc.)
   * @param {String} propertyDeckardID Encoded property Deckard ID
   * @param {*} params Query params
   * @returns API response
   */
  async retrieve(path, propertyDeckardID, params = {}) {
    const { queryParams, ...otherParams } = params;
    const queryParamsAndMode = { ...this.getModeKeyValues(), ...queryParams };
    await this.setToken(path);

    /**
     * Strips "/" from the beginning and end of the string if it exists.
     * As the string coming in is usually surrounded by the "/" character
     *  and building a full URL is confusing at times when unsure whether
     *  the incoming "path" arg already has a "/" at the end or not.
     *
     * e.g. "/property/" -> "/property"
     */
    const apiRoute = path.replace(/^\/+|\/+$/g, '');
    const apiEndpoint = this.getDataDomain();
    const url = `${apiEndpoint}/${apiRoute}/${propertyDeckardID}`;

    return this.axiosInstance.get(url, {
      params: queryParamsAndMode,
      ...otherParams,
    });
  }

  /**
   * Performs a POST on an endpoint of STR Oracle API.
   *
   * @param {String} path The API route. ("/property", "/property/", "property", "/property/tot", etc.)
   * @param {String} propertyDeckardID Encoded property Deckard ID
   * @param {URLSearchParams | undefined} queryParameters Query parameters for the POST
   * @param {*} body JSON body for the POST
   * @param {*} responseType Response type
   * @returns API Response
   */
  async submit(
    path,
    propertyDeckardID,
    queryParameters = {},
    body,
    responseType = ''
  ) {
    const bodyJSON = body?.toJSON ? body.toJSON() : body;

    await this.setToken();

    /**
     * Strips "/" from the beginning and end of the string if it exists.
     * As the string coming in is usually surrounded by the "/" character
     *  and building a full URL is confusing at times when unsure whether
     *  the incoming "path" arg already has a "/" at the end or not.
     *
     * e.g. "/property/" -> "/property"
     */
    const apiRoute = path.replace(/^\/+|\/+$/g, '');
    const apiEndpoint = this.getDataDomain();
    const url = `${apiEndpoint}/${apiRoute}/${propertyDeckardID}`;

    return this.axiosInstance.post(url, bodyJSON, {
      responseType,
      params: queryParameters,
    });
  }
}

export const strOracleApiSender = new StrOracleApiSender();
